import type { CrossFitClass, CrossFitPayload } from './api';

/**
 * Reading a CrossFit snapshot.
 *
 * One snapshot describes one local date (ADR-021). Bookings on later dates carry
 * their own date and must never read as today's. `null` means "not read" and is
 * never an empty list; `[]` means read and empty. A partial roster is never a
 * complete one. Both payload versions are supported: v1 (browser reader, the
 * rollback path) and v2 (phone capture), including v2 cards written with
 * `classes: null`.
 */

export type TodayState =
  // fromSchedule: the booking list was not read, but the class schedule of the
  // same capture marks these classes as reserved.
  | { kind: 'booked'; classes: CrossFitClass[]; fromSchedule?: boolean }
  | { kind: 'waitlisted'; classes: CrossFitClass[] }
  | { kind: 'mixed'; classes: CrossFitClass[] }
  | { kind: 'none' }
  | { kind: 'unknown'; reason: string };

/**
 * Reservations the class schedule shows ("reserved" on a time slot), as
 * bookings, for when the booking list itself was not read. The schedule and the
 * booking list are two screens of the same capture; the schedule's mark is
 * evidence of a reservation, but it carries no roster.
 */
export function scheduleBookings(p: CrossFitPayload): CrossFitClass[] {
  return (p.schedule ?? [])
    .filter((e) => e.reserved === true)
    .map((e) => ({
      class_key: `schedule:${e.time_start}`,
      time_start: e.time_start,
      time_end: e.time_end ?? null,
      class_type: e.class_type,
      own_status: 'reserved' as const,
      roster_status: 'unavailable' as const,
      capacity: e.capacity ?? null,
      participants: null,
      waitlist: null,
    }));
}

/** Whether today's booking list was read (a read, empty list counts). */
function bookingsRead(p: CrossFitPayload): boolean {
  if (p.registration_state === 'unknown') return false;
  if (p.classes === null || p.classes === undefined) return false;
  if ((p.registration_state === 'reserved' || p.registration_state === 'waitlisted' || p.registration_state === 'mixed') && p.classes.length === 0) return false;
  return true;
}

export function todayState(p: CrossFitPayload): TodayState {
  if (p.collection_status === 'failed') {
    return { kind: 'unknown', reason: 'Die Anmeldungen konnten nicht gelesen werden.' };
  }
  if (!bookingsRead(p)) {
    const fromSchedule = scheduleBookings(p);
    if (fromSchedule.length > 0) return { kind: 'booked', classes: fromSchedule, fromSchedule: true };
  }
  const classes = p.classes;
  const reserved = (classes ?? []).filter((c) => c.own_status === 'reserved');
  const waiting = (classes ?? []).filter((c) => c.own_status === 'waitlisted');

  switch (p.registration_state) {
    case 'unknown':
      return { kind: 'unknown', reason: 'Der Anmeldestatus konnte nicht ermittelt werden.' };
    case 'none':
      return { kind: 'none' };
    case 'reserved':
    case 'waitlisted':
    case 'mixed':
      if (!classes || classes.length === 0) {
        // The state says booked but the class details were not read: say what is known.
        return { kind: 'unknown', reason: 'Angemeldet, aber die Klassendetails wurden nicht gelesen.' };
      }
      break;
    default:
      break;
  }

  if (classes === null || classes === undefined) {
    return { kind: 'unknown', reason: 'Die Anmeldungen für diesen Tag wurden nicht gelesen.' };
  }
  if (reserved.length > 0 && waiting.length > 0) return { kind: 'mixed', classes };
  if (reserved.length > 0) return { kind: 'booked', classes: reserved };
  if (waiting.length > 0) return { kind: 'waitlisted', classes: waiting };
  // Read and empty: a day without a booking is information, not a failure.
  return { kind: 'none' };
}

/**
 * The headline for the snapshot's day. `isToday` is false for a snapshot of
 * another date, which must not read as today's state.
 */
export function todayHeadline(state: TodayState, isToday = true): string {
  const day = isToday ? 'Heute ' : '';
  const cap = (t: string) => t.charAt(0).toUpperCase() + t.slice(1);
  switch (state.kind) {
    case 'booked':
      return cap(`${day}angemeldet · ${state.classes.map((c) => c.time_start).join(', ')}${state.fromSchedule ? ' (laut Kursplan)' : ''}`);
    case 'waitlisted':
      return cap(`${day}auf der Warteliste · ${state.classes.map((c) => c.time_start).join(', ')}`);
    case 'mixed':
      return cap(`${day}angemeldet und auf der Warteliste`);
    case 'none':
      return cap(`${day}keine Anmeldung`);
    case 'unknown':
      return 'Anmeldestatus unbekannt';
  }
}

export interface Booking extends CrossFitClass {
  local_date: string;
  isToday: boolean;
}

/** Today's bookings followed by later ones, each with its own date, in time order. */
export function allBookings(p: CrossFitPayload): Booking[] {
  const own = bookingsRead(p) ? (p.classes ?? []) : scheduleBookings(p);
  const today: Booking[] = own.map((c) => ({ ...c, local_date: p.local_date, isToday: true }));
  const later: Booking[] = (p.upcoming ?? []).map((u) => ({ ...u, isToday: u.local_date === p.local_date }));
  return [...today, ...later].sort(
    (a, b) => a.local_date.localeCompare(b.local_date) || a.time_start.localeCompare(b.time_start),
  );
}

/**
 * The next booking from `now` on: a remaining class today, or the first later one.
 * `nowLocal` is { day: 'YYYY-MM-DD', time: 'HH:MM' } in Europe/Zurich.
 */
export function nextBooking(p: CrossFitPayload, nowLocal: { day: string; time: string }): Booking | null {
  for (const b of allBookings(p)) {
    if (b.local_date > nowLocal.day) return b;
    if (b.local_date === nowLocal.day && b.time_start >= nowLocal.time) return b;
  }
  return null;
}

export type RosterState =
  | { kind: 'not-read' }
  | { kind: 'empty' }
  | { kind: 'complete'; count: number }
  | { kind: 'partial'; count: number }
  | { kind: 'unavailable' };

export function rosterState(c: CrossFitClass): RosterState {
  if (c.roster_status === 'unavailable') return { kind: 'unavailable' };
  if (c.participants === null || c.participants === undefined) return { kind: 'not-read' };
  if (c.roster_status === 'partial') return { kind: 'partial', count: c.participants.length };
  if (c.participants.length === 0) return { kind: 'empty' };
  return { kind: 'complete', count: c.participants.length };
}

export function rosterText(c: CrossFitClass): string {
  const r = rosterState(c);
  const of = c.capacity ? ` von ${c.capacity}` : '';
  switch (r.kind) {
    case 'not-read':
      return 'Teilnehmerliste nicht gelesen';
    case 'unavailable':
      return 'Teilnehmerliste nicht verfügbar';
    case 'empty':
      return `Liste gelesen: noch niemand eingetragen${of ? ` (0${of})` : ''}`;
    case 'complete':
      return `${r.count} Teilnehmende${of} · Liste vollständig`;
    case 'partial':
      return `${r.count} Teilnehmende bekannt${of} · Liste unvollständig`;
  }
}

export function waitlistText(c: CrossFitClass): string {
  if (c.waitlist === null || c.waitlist === undefined) return 'Warteliste unbekannt';
  if (c.waitlist.length === 0) return 'Warteliste leer';
  return `${c.waitlist.length} auf der Warteliste`;
}

const BASIS_LABELS: Record<string, string> = {
  archive_upload_time: 'Hochladezeit des Archivs',
  archive_modified_time: 'Änderungszeit des Archivs',
  file_modified_time: 'Änderungszeit der Datei',
  collector_time: 'Zeit der Abholung',
  screen_time: 'angezeigte Uhrzeit',
};

export function captureBasisLabel(basis: string | null | undefined): string | null {
  if (!basis) return null;
  return BASIS_LABELS[basis] ?? basis.replace(/_/g, ' ');
}

/** Workout text is shown as captured: whitespace, units, reps and order intact. */
export function workoutTeaser(body: string, lines = 4): { text: string; truncated: boolean } {
  const all = body.split('\n');
  if (all.length <= lines && body.length <= 260) return { text: body, truncated: false };
  let text = all.slice(0, lines).join('\n');
  if (text.length > 260) text = text.slice(0, 260).replace(/\s+\S*$/, '');
  return { text, truncated: true };
}
